import { z } from 'zod';
import {
  WorkspaceId,
  ArtistId,
  GigId,
  BriefId,
  ImprovProfileId,
  RhythmCatalogId,
  CatalogEntryId,
  AudienceSessionId,
} from '../ids.js';
import { IsoTimestamp, Version, SourceCode, Timestamps } from '../primitives.js';
import {
  AudienceMode,
  FieldPolicyKind,
  ResolverPolicy,
  PerformerStatus,
  ModerationState,
} from '../enums.js';

/** Improv dimensions the domain must support (§20.3). */
export const ImprovDimension = z.enum([
  'tempoCategory',
  'exactBpm',
  'tempoRange',
  'tonic',
  'mode',
  'meter',
  'grooveFamily',
  'rhythmPattern',
  'rhythmVariation',
  'drumKit',
  'energy',
  'mood',
  'genre',
  'topic',
  'requiredPhrase',
  'instrumentEmphasis',
  'performerNote',
]);
export type ImprovDimension = z.infer<typeof ImprovDimension>;

/** Per-dimension artist policy (§20.2). */
export const FieldPolicy = z.object({
  dimension: ImprovDimension,
  kind: FieldPolicyKind,
  allowedChoices: z.array(z.string()).default([]),
  defaultValue: z.string().nullable().default(null),
  required: z.boolean().default(false),
  helpText: z.string().max(280).optional(),
  artistCanOverride: z.boolean().default(true),
});
export type FieldPolicy = z.infer<typeof FieldPolicy>;

export const MeterCompat = z.object({
  numerator: z.number().int().positive(),
  denominator: z.number().int().positive(),
});
export type MeterCompat = z.infer<typeof MeterCompat>;

/** §20.9 Rhythm-catalog entry. Seed only verified data; never fabricate names (§20.8). */
export const RhythmCatalogEntry = z.object({
  catalogEntryId: CatalogEntryId,
  deviceProfileKey: z.string().min(1),
  catalogVersion: Version.default(1),
  externalId: z.string().optional(),
  displayName: z.string().min(1).max(120),
  family: z.string().min(1).max(60),
  meter: MeterCompat,
  recommendedTempo: z.object({ min: z.number().int(), max: z.number().int() }),
  variations: z.array(z.string()).default([]),
  allowedKitIds: z.array(z.string()).default([]),
  aliases: z.array(z.string()).default([]),
  source: z.enum(['verified_device_catalog', 'artist_defined', 'imported']),
  active: z.boolean().default(true),
});
export type RhythmCatalogEntry = z.infer<typeof RhythmCatalogEntry>;

/** §27.16 RhythmCatalog. */
export const RhythmCatalog = Timestamps.extend({
  rhythmCatalogId: RhythmCatalogId,
  workspaceId: WorkspaceId.nullable().default(null),
  artistId: ArtistId.nullable().default(null),
  deviceProfileKey: z.string().min(1),
  catalogVersion: Version.default(1),
  name: z.string().min(1).max(120),
  source: z.enum(['verified_device_catalog', 'artist_defined', 'imported']),
  status: z.enum(['active', 'archived']),
  entries: z.array(RhythmCatalogEntry).default([]),
});
export type RhythmCatalog = z.infer<typeof RhythmCatalog>;

/** §27.14 ImprovProfile. */
export const ImprovProfile = Timestamps.extend({
  improvProfileId: ImprovProfileId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  name: z.string().min(1).max(120),
  profileVersion: Version.default(1),
  status: z.enum(['draft', 'active', 'archived']),
  audienceMode: AudienceMode.default('general'),
  advancedDrilldown: z.boolean().default(false),
  fieldPolicies: z.array(FieldPolicy).default([]),
  compatibilityRules: z.array(z.record(z.string(), z.unknown())).default([]),
  resolverPolicy: ResolverPolicy.default('artist_review'),
  summaryTemplate: z.string().max(280).optional(),
  deviceProfileRef: z.string().nullable().default(null),
  notificationPolicy: z.record(z.string(), z.unknown()).default({}),
  limits: z.record(z.string(), z.unknown()).default({}),
});
export type ImprovProfile = z.infer<typeof ImprovProfile>;

/** Normalized resolved fields on a brief (§20.12). */
export const ResolvedTempo = z.object({
  label: z.string().nullable().default(null),
  minBpm: z.number().int().nullable().default(null),
  maxBpm: z.number().int().nullable().default(null),
  targetBpm: z.number().int().nullable().default(null),
  resolution: ResolverPolicy,
});
export type ResolvedTempo = z.infer<typeof ResolvedTempo>;
export const ResolvedKey = z.object({
  tonic: z.string(),
  mode: z.string(),
  display: z.string(),
});
export type ResolvedKey = z.infer<typeof ResolvedKey>;
export const ResolvedMeter = z.object({
  numerator: z.number().int().positive(),
  denominator: z.number().int().positive(),
  display: z.string(),
});
export type ResolvedMeter = z.infer<typeof ResolvedMeter>;
export const ResolvedRhythm = z.object({
  family: z.string(),
  deviceProfileKey: z.string().nullable().default(null),
  catalogEntryId: CatalogEntryId.nullable().default(null),
  variation: z.string().nullable().default(null),
  kitId: z.string().nullable().default(null),
  resolution: ResolverPolicy,
});
export type ResolvedRhythm = z.infer<typeof ResolvedRhythm>;

export const ResolvedBrief = z.object({
  tempo: ResolvedTempo.optional(),
  key: ResolvedKey.optional(),
  meter: ResolvedMeter.optional(),
  rhythm: ResolvedRhythm.optional(),
});
export type ResolvedBrief = z.infer<typeof ResolvedBrief>;

/** Immutable record of a performer edit (§20.13). */
export const BriefEdit = z.object({
  at: IsoTimestamp,
  by: z.string(),
  field: z.string(),
  from: z.unknown(),
  to: z.unknown(),
});
export type BriefEdit = z.infer<typeof BriefEdit>;

/** §20.12 / §27.15 ImprovBrief — stores raw audience choices and normalized values. */
export const ImprovBrief = z.object({
  briefId: BriefId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  gigId: GigId,
  audienceSessionId: AudienceSessionId,
  profileId: ImprovProfileId,
  profileVersion: Version,
  audienceMode: AudienceMode,
  /** Immutable raw audience choices (§20.13). */
  rawChoices: z.record(z.string(), z.string()),
  resolved: ResolvedBrief,
  topic: z.string().max(200).nullable().default(null),
  friendlySummary: z.string().max(280),
  privatePerformerNote: z.string().max(2000).nullable().default(null),
  moderationState: ModerationState.default('clean'),
  status: PerformerStatus.default('new'),
  sourceCode: SourceCode.nullable().default(null),
  derivedFromBriefId: BriefId.nullable().default(null),
  editHistory: z.array(BriefEdit).default([]),
  submittedAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
  version: Version,
});
export type ImprovBrief = z.infer<typeof ImprovBrief>;
